/** @jsx jsx */

import React, {
  Fragment as _,
  useRef,
  useLayoutEffect,
  useEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import { values, colors } from 'styles/variables';
import useImageLoader from 'hooks/useImageLoader';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import { useSelector } from 'react-redux';
import { State } from 'store/store';

const { headerHeight } = values;
const { lPink, lGrey4 } = colors;

const Circle = ({
  id,
  src,
  fit = 'cover',
  cachedSrc,
  children,
}: {
  id: string | number;
  src: string;
  fit?: string;
  cachedSrc?: any;
  children?: any;
}) => {
  const imgLoadedState = useImageLoader(src);
  //

  return imgLoadedState === 1 ? (
    <LoadingSpinner />
  ) : (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          maxWidth: '50vh',
          maxHeight: '50vh',
        }}
      >
        <div
          css={css({
            position: 'relative',
            width: '100%',
            paddingTop: '100%',
            background: `url('${src}') no-repeat, ${lGrey4}`,
            backgroundPosition: 'center',
            backgroundSize: fit,
            borderRadius: '50%',
            img: {
              position: 'absolute',
              left: 0,
              top: 0,
              width: 0,
              height: 0,
              opacity: 0,
            },
          })}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const cachedCircleSize = {};

interface ICircleCalculatedProps {
  id: string;
  src?: string;
  cachedSrc?: any;
  fit?: string;
  alt: string;
  linkData?: {
    url: string;
    title: string;
    target: string;
  };
}

const CircleCalculated = (props: ICircleCalculatedProps) => {
  let { id, src: placeholder, alt, linkData } = props;

  const { src = placeholder, fit = 'cover' } = useSelector((state: State) => {
    return state.blocks[id].props!;
  });

  const imgLoadedState = useImageLoader(src);
  //

  const init = useRef<boolean>(false);
  const container = useRef<HTMLDivElement>(null);
  const circle = useRef<HTMLAnchorElement | HTMLSpanElement | null>(null);
  const img = useRef<HTMLImageElement>(null);
  const hyperLink = useRef<HTMLAnchorElement>(null);

  const pageBackgroundColor = useRef<string>('#fff');
  const aHiliteColor = useRef<string>('#060709');

  useLayoutEffect(() => {
    const tick = setInterval(() => {
      if (container.current && circle.current && img.current) {
        //
        const parentNode = container.current.parentNode as HTMLDivElement;
        const parentHeight = parentNode.offsetHeight;
        const circleWidth = img.current.offsetWidth;
        const circleHeight = img.current.offsetHeight;
        const useParentHeight = parentHeight < circleWidth;

        container.current.style.minHeight = `${circleHeight}px`;

        cachedCircleSize[id] = `${
          useParentHeight ? parentHeight : circleWidth
        }px`;

        circle.current.style.width = circle.current.style.height =
          cachedCircleSize[id];

        if (
          !init.current &&
          circle.current.style.height === cachedCircleSize[id]
        ) {
          setTimeout(() => {
            if (circle.current) {
              circle.current.style.opacity = '1';
            }
            init.current = true;
          }, 1000 / 12);
        }

        //
        if (hyperLink.current) {
          hyperLink.current.style.width = hyperLink.current.style.height =
            cachedCircleSize[id];
        }
      }
    }, 1000 / 24);

    return () => {
      clearInterval(tick);
    };
  }, []);

  useEffect(() => {
    if (!container.current) {
      return;
    }

    const section = container.current.closest('section') as HTMLElement;

    if (!section) {
      return;
    }

    pageBackgroundColor.current = window.getComputedStyle(
      section
    ).backgroundColor;

    if (pageBackgroundColor.current === 'rgba(0, 0, 0, 0)') {
      pageBackgroundColor.current = '#fff';
    }

    const button = section?.querySelector<HTMLButtonElement>(
      '[data-as-exit-btn]'
    );

    if (button) {
      // get find button background color
      const buttonStyles = window.getComputedStyle(button);
      aHiliteColor.current = buttonStyles.backgroundColor;
    }
  }, []);

  // will conditionally wrap with <a /> if link data is present
  const hasLinkData = linkData && linkData?.url && linkData?.url?.length > 0;

  const circleImgDisplayStyles = {
    position: 'relative',

    ...(cachedCircleSize[id] && {
      width: cachedCircleSize[id],
      height: cachedCircleSize[id],
    }),

    background: `url("${src}") no-repeat`,
    backgroundSize: fit,
    backgroundPosition: 'center',
    borderRadius: '50%',
    opacity: cachedCircleSize[id] ? 1 : 0,
  };

  return imgLoadedState === 1 ? (
    <LoadingSpinner />
  ) : (
    <div
      ref={container}
      className="circle-img"
      css={css({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',

        a: {
          cursor: 'pointer',
          transition: 'box-shadow 0.1s',
          ...(window.dev &&
            window.dev() && {
              pointerEvents: 'none',
            }),
        },

        'a:hover': {
          boxShadow: `0 0 0 2px ${pageBackgroundColor.current ||
            '#fff'}, 0 0 0 3px ${aHiliteColor.current} !important`,
        },

        'a:focus': {
          boxShadow: `0 0 0 1px ${aHiliteColor.current}, 0 0 0 3px ${pageBackgroundColor.current}, 0 0 0 4px ${aHiliteColor.current} !important`,
        },
      })}
    >
      <img
        ref={img}
        src={src}
        style={{
          position: 'absolute',
          width: '100%',
          maxWidth: 'calc(50vh)',
          opacity: 0,
        }}
        alt={alt}
      />

      {hasLinkData ? (
        <a
          ref={circle as React.LegacyRef<HTMLAnchorElement>}
          href={linkData && linkData.url}
          title={linkData && linkData.title}
          target={linkData && linkData.target}
          tabIndex={linkData && 0}
          style={circleImgDisplayStyles}
        />
      ) : (
        <span ref={circle} style={circleImgDisplayStyles} />
      )}
    </div>
  );
};

export default CircleCalculated;
export { CircleCalculated };
